import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Crown, ArrowRight } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ComparisonsIndexPage = () => {
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/piece-comparisons`);
        if (!cancelled) setComparisons(res.data.comparisons || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="comparisons-index-page" className="bg-[#FAF9F6]">
      <SEO
        title="Pre-Owned Luxury Jewelry Comparisons — Head-to-Head Buyer Guides"
        description="Cartier Love vs Juste un Clou. Van Cleef Vintage vs Magic Alhambra. Tiffany vs Cartier vs Harry Winston engagement. Six decisive head-to-head buyer comparisons."
        url="/vs"
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{ items: [
          { name: 'Home', url: '/' },
          { name: 'Comparisons', url: '/vs' },
        ]}}
      />

      <section className="bg-[#111] text-white">
        <div className="container-editorial pt-20 pb-16 lg:pt-28 lg:pb-20">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Comparisons</span>
          </nav>
          <p className="overline text-[#C69C6D] mb-6">Head-to-Head Guides</p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            The <span className="italic">Comparisons</span>
          </h1>
          <p className="font-heading italic text-xl lg:text-2xl text-white/85 max-w-3xl">
            Six head-to-head buyer guides. Real prices. Real ten-year CAGRs. A decisive editor&apos;s pick on every one.
          </p>
        </div>
      </section>

      <section className="section-editorial">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12">
            {comparisons.map((c) => (
              <Link
                key={c.slug}
                to={`/vs/${c.slug}`}
                className="group block bg-white border border-[#E5E0D8] hover:border-[#111] transition-all p-8"
                data-testid={`comparison-card-${c.slug}`}
              >
                <p className="overline overline-gold mb-4">{c.category} · {c.piece_count} pieces</p>
                <h2 className="font-heading text-2xl lg:text-3xl text-[#111] font-light leading-tight mb-4 group-hover:text-[#C69C6D] transition-colors">
                  {c.title}
                </h2>
                <p className="text-[#555] leading-relaxed mb-6 line-clamp-2">{c.intro}</p>
                <div className="flex items-center justify-between pt-6 border-t border-[#E5E0D8]">
                  <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-[#C69C6D]">
                    <Crown className="w-3 h-3" strokeWidth={1.75} /> Winner: {c.winner}
                  </span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-[#111]" strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparisonsIndexPage;
